import axios from "axios";

const url = process.env.REACT_APP_API_URL;
const links = {
    login: "/api/auth/login",
    addProduct: { method: "POST", url: "/api/products/add" },
    addCourse: { method: "POST", url: "/api/courses" },
    addService: { method: "POST", url: "/api/services" },
    updateAppointment: { method: "PATCH", url: "/api/appointments" },
    updateCourse: { method: "PATCH", url: "/api/courses" },
    updateProduct: { method: "PATCH", url: "/api/products" },
    updateService: { method: "PATCH", url: "/api/services" },
    options: "/api"
};

/**
 * Fetch appointments and transform them into calendar events format.
 * @param {Function} setEvents - Function to update the state with fetched events.
 */
export async function fetchAppointments(setEvents) {
    try {
        const response = await axios.get(`${url}/api/appointments`);
        const appointments = response.data;

        // Transform the appointments to the event format needed for the calendar
        const formattedEvents = appointments.map(appointment => ({
            title: `${appointment.serviceId ? appointment.serviceId.name : 'others'} - ${appointment.customerName} (${appointment.customerPhone})`,
            start: new Date(appointment.startTime),
            end: new Date(appointment.endTime),
            allDay: false,
            className: appointment.serviceId ? 'service' : 'other', // Adjust based on your criteria
        }));
        setEvents(formattedEvents);
    } catch (error) {
        console.error('Error fetching appointments:', error);
    }
}

/**
 * Login function to authenticate a user.
 * @param {string} email - User's email address.
 * @param {string} password - User's password.
 * @returns {Promise<string>} - Returns a JWT token if authentication is successful.
 */
export async function login(email, password) {
    try {
        const response = await fetch(`${url}${links.login}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, password }),
        });

        if (!response.ok) {
            throw new Error('Invalid email or password');
        }
        const data = await response.json();
        if (data.isAdmin) {
            return data.token; // Adjust this based on your API response structure
        } else {
            return { message: "User does not have access to this website" };
        }
    } catch (error) {
        throw new Error(error.message);
    }
}
/**
 * Check if the currently stored token is still valid.
 * Returns an object like { valid: true, isAdmin: true } or false if invalid.
 */
export async function checkToken() {
    const token = localStorage.getItem('token');
    if (!token) return false; // No token => not logged in

    try {
        const response = await fetch(`${url}/api/auth/check-token`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        if (!response.ok) {
            // e.g. 401 or 403 => invalid token or no access
            localStorage.removeItem('token');
            return false;
        }

        const data = await response.json();
        // data might look like { valid: true, isAdmin: true, userId, email }
        return data;
    } catch (err) {
        console.error(err);
        localStorage.removeItem('token');
        return false;
    }
}
/**
 * Function to add a product.
 * @param {Object} product - Product details including images.
 * @param {string} token - JWT token for authentication.
 * @returns {Promise<Object>} - Returns the added product details.
 */
export async function addProduct(product) {
    try {
        const formData = new FormData();
        formData.append('name', product.name);
        formData.append('description', product.description);
        formData.append('price', product.price);
        formData.append('quantity', product.quantity);

        if (product.files && product.files.length > 0) {
            product.files.forEach((image, index) => {
                formData.append('images', image);
            });
        }

        const response = await fetch(`${url}${links.addProduct.url}`, {
            method: links.addProduct.method,
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
            },
            body: formData,
        });

        if (!response.ok) {
            throw new Error('Failed to add product');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        throw new Error(error.message);
    }
}

/**
 * Function to add a course.
 * @param {Object} course - Course details including images.
 * @param {string} token - JWT token for authentication.
 * @returns {Promise<Object>} - Returns the added course details.
 */
export async function addCourse(course) {
    try {
        const formData = new FormData();
        formData.append('title', course.title);
        formData.append('description', course.description);
        formData.append('price', course.price);
        formData.append('duration', course.duration);

        if (course.files && course.files.length > 0) {
            course.files.forEach((image, index) => {
                formData.append('images', image);
            });
        }

        const response = await fetch(`${url}${links.addCourse.url}`, {
            method: links.addCourse.method,
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
            },
            body: formData,
        });

        if (!response.ok) {
            throw new Error('Failed to add course');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        throw new Error(error.message);
    }
}

/**
 * Function to add a service.
 * @param {Object} service - Service details including images.
 * @param {string} token - JWT token for authentication.
 * @returns {Promise<Object>} - Returns the added service details.
 */
export async function addService(service) {
    try {
        const formData = new FormData();
        formData.append('name', service.name);
        formData.append('description', service.description);
        formData.append('price', service.price);
        formData.append('duration', service.duration);

        if (service.files && service.files.length > 0) {
            service.files.forEach((image, index) => {
                formData.append('images', image);
            });
        }

        const response = await fetch(`${url}${links.addService.url}`, {
            method: links.addService.method,
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
            },
            body: formData,
        });

        if (!response.ok) {
            throw new Error('Failed to add service');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        throw new Error(error.message);
    }
}

/**
 * Fetch options for the second select based on the first select value.
 * @param {string} endpoint - The endpoint to fetch data from.
 * @returns {Promise<Array>} - Returns an array of options for the second select.
 */
export async function fetchOptions(endpoint) {
    try {
        const response = await fetch(`${url + links.options}/${endpoint}`);
        if (!response.ok) {
            throw new Error('Failed to fetch options');
        }
        const data = await response.json();
        return data;
    } catch (error) {
        throw new Error(error.message);
    }
}

/**
 * Function to add an appointment.
 * @param {Object} appointment - Appointment details.
 * @returns {Promise<Object>} - Returns the added appointment details.
 */
export async function addAppointment(appointment) {
    try {
        const response = await fetch(`${url}/api/appointments`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
            },
            body: JSON.stringify(appointment),
        });

        if (!response.ok) {
            const errorData = await response.json();
            const errorMessage = errorData.message || 'Failed to add appointment';
            throw new Error(errorMessage);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        throw new Error(error.message);
    }
}


/**
 * Function to update an appointment.
 * @param {Object} body - Object containing appointment ID and updated details.
 * @param {string} body.id - Appointment ID.
 * @param {Object} body.updates - Updated appointment details.
 * @returns {Promise<Object>} - Returns the updated appointment details.
 */
export async function updateAppointment(body) {
    try {
        const { id, updates } = body;
        const response = await fetch(`${url}${links.updateAppointment.url}/${id}`, {
            method: links.updateAppointment.method,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify(updates),
        });

        if (!response.ok) {
            const errorData = await response.json();
            const errorMessage = errorData.message || 'Failed to update appointment';
            throw new Error(errorMessage);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        throw new Error(error.message);
    }
}

/**
 * Function to update a course.
 * @param {Object} body - Object containing course ID, updated details, files, and images to remove.
 * @param {string} body.id - Course ID.
 * @param {Object} body.updates - Updated course details.
 * @param {Array} [body.files] - New images to upload (optional).
 * @param {Array} [body.removeImages] - IDs of images to remove (optional).
 * @returns {Promise<Object>} - Returns the updated course details.
 */
export async function updateCourse(body) {
    try {
        const { id, updates, files = [], removeImages = [] } = body;
        const formData = new FormData();

        // Append course details
        formData.append('title', updates.title || '');
        formData.append('description', updates.description || '');
        formData.append('price', updates.price || '');
        formData.append('duration', updates.duration || '');

        // Append files if provided
        if (files.length > 0) {
            files.forEach((image) => {
                formData.append('images', image);
            });
        }

        // Append images to remove if provided
        if (removeImages.length > 0) {
            formData.append('removeImages', JSON.stringify(removeImages));
        }

        const response = await fetch(`${url}${links.updateCourse.url}/${id}`, {
            method: links.updateCourse.method,
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
            },
            body: formData,
        });

        if (!response.ok) {
            throw new Error('Failed to update course');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        throw new Error(error.message);
    }
}

/**
 * Function to update a product.
 * @param {Object} body - Object containing product ID, updated details, files, and images to remove.
 * @param {string} body.id - Product ID.
 * @param {Object} body.updates - Updated product details.
 * @param {Array} [body.files] - New images to upload (optional).
 * @param {Array} [body.removeImages] - IDs of images to remove (optional).
 * @returns {Promise<Object>} - Returns the updated product details.
 */
export async function updateProduct(body) {
    try {
        const { id, updates, files = [], removeImages = [] } = body;
        const formData = new FormData();

        // Append product details
        formData.append('name', updates.name || '');
        formData.append('description', updates.description || '');
        formData.append('price', updates.price || '');
        formData.append('quantity', updates.quantity || '');

        // Append files if provided
        if (files.length > 0) {
            files.forEach((image) => {
                formData.append('images', image);
            });
        }

        // Append images to remove if provided
        if (removeImages.length > 0) {
            formData.append('removeImages', JSON.stringify(removeImages));
        }

        const response = await fetch(`${url}${links.updateProduct.url}/${id}`, {
            method: links.updateProduct.method,
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
            },
            body: formData,
        });

        if (!response.ok) {
            throw new Error('Failed to update product');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        throw new Error(error.message);
    }
}

/**
 * Function to update a service.
 * @param {Object} body - Object containing service ID, updated details, files, and images to remove.
 * @param {string} body.id - Service ID.
 * @param {Object} body.updates - Updated service details.
 * @param {Array} [body.files] - New images to upload (optional).
 * @param {Array} [body.removeImages] - IDs of images to remove (optional).
 * @returns {Promise<Object>} - Returns the updated service details.
 */
export async function updateService(body) {
    try {
        const { id, updates, files = [], removeImages = [] } = body;
        const formData = new FormData();

        // Append service details
        formData.append('name', updates.name || '');
        formData.append('description', updates.description || '');
        formData.append('price', updates.price || '');
        formData.append('duration', updates.duration || '');

        // Append files if provided
        if (files.length > 0) {
            files.forEach((image) => {
                formData.append('images', image);
            });
        }

        // Append images to remove if provided
        if (removeImages.length > 0) {
            formData.append('removeImages', JSON.stringify(removeImages));
        }

        const response = await fetch(`${url}${links.updateService.url}/${id}`, {
            method: links.updateService.method,
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
            },
            body: formData,
        });

        if (!response.ok) {
            throw new Error('Failed to update service');
        }

        const data = await response.json();
        return data;
    } catch (error) {
        throw new Error(error.message);
    }
}